const STORAGE_KEY = "sg_attribution";

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  referrer?: string;
  landingPath?: string;
  ts?: string;
}

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"] as const;
const UTM_TO_FIELD: Record<(typeof UTM_PARAMS)[number], keyof Attribution> = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
};

/**
 * First-touch marketing attribution. Writes to sessionStorage once, on the first page load
 * of a session that carries a UTM param or an external referrer — never overwrites an
 * existing value, and never writes anything at all for an ordinary direct/internal visit.
 * Deliberately conservative for PECR: session-scoped only, one first-party key, nothing
 * written unless there's a genuine external signal to record. See AboutPage FAQs.
 */
export function captureAttribution(
  url: URL = new URL(window.location.href),
  referrer: string = document.referrer,
): void {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return; // first touch already recorded

    const attribution: Attribution = {};
    for (const param of UTM_PARAMS) {
      const value = url.searchParams.get(param);
      if (value) attribution[UTM_TO_FIELD[param]] = value.slice(0, 200);
    }

    const isExternalReferrer = referrer && !referrer.startsWith(url.origin);
    if (isExternalReferrer) attribution.referrer = referrer.slice(0, 500);

    const hasSignal = Object.keys(attribution).length > 0;
    if (!hasSignal) return;

    attribution.landingPath = `${url.pathname}${url.search}`.slice(0, 500);
    attribution.ts = new Date().toISOString();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // sessionStorage can throw (private browsing, blocked site data) - attribution is a
    // nice-to-have, never let it break the app.
  }
}

/** Reads back whatever captureAttribution stored this session, if anything. */
export function getStoredAttribution(): Attribution | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Landing on /join/:code is its own meaningful first-touch signal — being invited via a link
 * — independent of whether a UTM param or Referer header happens to be present. captureAttribution
 * alone misses this: links opened from WhatsApp/iMessage frequently carry neither, so its
 * hasSignal check would find nothing and write nothing at all, losing the one case this
 * referral mechanism cares about most. Same first-touch-wins semantics as captureAttribution
 * (never overwrites an existing value this session) so call order between the two doesn't
 * matter. The server resolves the join code to a referring admin itself — this never sends or
 * exposes who that is.
 */
export function captureJoinLanding(code: string): void {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return; // first touch already recorded
    const attribution: Attribution = {
      landingPath: `/join/${code}`.slice(0, 500),
      ts: new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // sessionStorage can throw (private browsing, blocked site data) - same as captureAttribution.
  }
}
